import React, { useState } from "react";
import { toast } from "sonner";
import {
    Wrench, MagnifyingGlass, CheckCircle, Warning, ArrowClockwise,
    LinkSimple, User,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";

/**
 * Admin — Legacy Parser Cleanup
 *
 * V1.3 P0 (Feb 2026) — Candidates uploaded before the autofill blacklist
 * hardening ended up with names like "Resume", "Objective", "Curriculum
 * Vitae", or Indian parentage lines ("S/o M.Velselvan"). This page lets an
 * admin scan for them and (optionally) auto-fix by re-running the name +
 * title picker against the stored `resume_text`.
 */
export default function AdminParserCleanup() {
    const [report, setReport] = useState(null); // last scan/apply response
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState(null); // "scan" | "apply"

    const run = async (apply) => {
        setLoading(true);
        setMode(apply ? "apply" : "scan");
        try {
            const { data } = await api.post(
                `/admin/candidates/reparse-legacy${apply ? "?apply=1" : ""}`,
            );
            setReport(data);
            if (apply) {
                toast.success(
                    `Fixed ${data.updated} candidate${data.updated === 1 ? "" : "s"}. ` +
                    `${data.affected - data.updated} still need manual review.`,
                );
            } else {
                toast.info(
                    `Scan complete — ${data.affected} broken record${data.affected === 1 ? "" : "s"} found ` +
                    `(${data.updated} auto-fixable).`,
                );
            }
        } catch (ex) {
            toast.error(formatApiError(ex) || "Cleanup request failed.");
        } finally {
            setLoading(false);
            setMode(null);
        }
    };

    return (
        <div className="max-w-5xl mx-auto p-4 sm:p-8" data-testid="admin-parser-cleanup-page">
            <div className="flex items-start gap-4 mb-8">
                <div className="w-12 h-12 bg-ink text-white flex items-center justify-center shrink-0">
                    <Wrench size={22} weight="bold" />
                </div>
                <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">
                        Admin · Data hygiene
                    </p>
                    <h1 className="font-heading text-4xl font-black tracking-tight text-ink">
                        Legacy parser cleanup
                    </h1>
                    <p className="text-sm text-[#525252] mt-2 max-w-2xl">
                        Some candidates uploaded before the resume-parser hardening
                        were saved with names like <span className="font-mono bg-[#F5F5F5] px-1">Resume</span>,
                        <span className="font-mono bg-[#F5F5F5] px-1"> Objective</span>, or
                        <span className="font-mono bg-[#F5F5F5] px-1"> S/o M.Velselvan</span>.
                        This tool scans your workspace and re-extracts the correct name
                        + title from each candidate&apos;s stored resume text.
                    </p>
                </div>
            </div>

            <div className="border border-[#0A0A0A] bg-white p-6 mb-6">
                <div className="flex flex-wrap items-center gap-3">
                    <button
                        type="button"
                        data-testid="parser-cleanup-scan-btn"
                        onClick={() => run(false)}
                        disabled={loading}
                        className="bg-ink hover:bg-cobalt disabled:opacity-60 text-white px-5 py-2.5 text-xs font-black uppercase tracking-wider inline-flex items-center gap-2 transition-colors"
                    >
                        <MagnifyingGlass size={14} weight="bold" />
                        {loading && mode === "scan" ? "Scanning…" : "Dry-run scan"}
                    </button>
                    <button
                        type="button"
                        data-testid="parser-cleanup-apply-btn"
                        onClick={() => {
                            if (!confirm(
                                "Apply the corrected names to all matched candidates? " +
                                "This writes to the database and cannot be undone."
                            )) return;
                            run(true);
                        }}
                        disabled={loading || !report || report.updated === 0}
                        title={!report ? "Run a scan first" : (report.updated === 0 ? "No auto-fixable records" : "")}
                        className="bg-[#059669] hover:bg-[#047857] disabled:opacity-40 disabled:cursor-not-allowed text-white px-5 py-2.5 text-xs font-black uppercase tracking-wider inline-flex items-center gap-2 transition-colors"
                    >
                        <ArrowClockwise size={14} weight="bold" />
                        {loading && mode === "apply" ? "Applying…" : "Apply fixes"}
                    </button>
                    <p className="text-[11px] text-[#A3A3A3] ml-auto">
                        Scan first, review the preview, then apply.
                    </p>
                </div>
            </div>

            {report && (
                <div data-testid="parser-cleanup-report" className="space-y-6">
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                        <StatCard label="Scanned" value={report.scanned} tone="neutral" />
                        <StatCard label="Records affected" value={report.affected} tone="warn" />
                        <StatCard
                            label={report.dry_run ? "Auto-fixable" : "Fixed"}
                            value={report.updated}
                            tone="ok"
                            testid="stat-updated"
                        />
                        <StatCard
                            label="Text de-noised"
                            value={report.text_cleaned || 0}
                            tone="ok"
                        />
                        <StatCard
                            label="Manual review"
                            value={Math.max(0, report.affected - report.updated)}
                            tone="warn"
                        />
                    </div>

                    {report.samples?.length > 0 && (
                        <div className="border border-[#E5E5E5] bg-white overflow-x-auto">
                            <div className="p-4 border-b border-[#E5E5E5] flex items-center justify-between gap-3">
                                <p className="text-[11px] font-black uppercase tracking-[0.2em] text-ink">
                                    Preview · first {report.samples.length} affected
                                </p>
                                <p className="text-[10px] text-[#A3A3A3]">
                                    {report.dry_run ? "Dry run — nothing written yet" : "Applied to database"}
                                </p>
                            </div>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] bg-[#FAFAFA]">
                                        <th className="px-3 py-2">Code</th>
                                        <th className="px-3 py-2">Current name</th>
                                        <th className="px-3 py-2">Proposed name</th>
                                        <th className="px-3 py-2">Proposed title</th>
                                        <th className="px-3 py-2 w-10"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {report.samples.map((s) => {
                                        // If name is already good and only text got de-noised,
                                        // show it kept (green) rather than "manual review".
                                        const nameKept = !s.new_name && s.text_cleaned;
                                        const nameFailed = !s.new_name && !s.text_cleaned;
                                        return (
                                        <tr
                                            key={s.id}
                                            data-testid={`sample-row-${s.id}`}
                                            className="border-t border-[#E5E5E5]"
                                        >
                                            <td className="px-3 py-2 font-mono text-[11px] text-[#525252]">{s.code}</td>
                                            <td className={`px-3 py-2 ${nameKept ? "text-[#059669]" : "text-[#DC2626] line-through"}`}>
                                                {s.old_name || "—"}
                                            </td>
                                            <td className="px-3 py-2 font-bold">
                                                {s.new_name ? (
                                                    s.new_name
                                                ) : nameKept ? (
                                                    <span className="text-[#525252] font-normal">
                                                        Name kept · resume text de-noised
                                                    </span>
                                                ) : (
                                                    <span className="text-[#A3A3A3] italic font-normal">
                                                        Manual review needed
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-[#525252]">
                                                {s.new_title || <span className="text-[#A3A3A3]">—</span>}
                                            </td>
                                            <td className="px-3 py-2">
                                                {(s.new_name || nameKept) ? (
                                                    <CheckCircle size={14} weight="fill" className="text-[#059669]" />
                                                ) : (
                                                    <Warning size={14} weight="fill" className="text-[#F59E0B]" />
                                                )}
                                            </td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {report.affected > 0 && report.updated < report.affected && (
                        <div className="border border-[#F59E0B]/50 bg-[#FFFBEB] p-4 text-sm text-[#78350F]">
                            <span className="font-bold">Note:</span> {report.affected - report.updated}{" "}
                            record{report.affected - report.updated === 1 ? "" : "s"} could not be auto-fixed —
                            usually because the stored resume text is empty or the true
                            name isn&apos;t on the first 10 lines. Open each candidate and use
                            the pencil-edit button to correct manually.
                        </div>
                    )}
                    {report.affected > 0 && report.updated === report.affected && (
                        <div className="border border-[#059669]/40 bg-[#ECFDF5] p-4 text-sm text-[#065F46]">
                            <span className="font-bold">All good.</span> Every affected
                            record has a proposed fix — click <span className="font-bold">Apply fixes</span> to persist.
                        </div>
                    )}
                </div>
            )}

            {/* Feb 11, 2026 — Repair Candidate Link panel. Used when a
                candidate registers but the recruiter view still shows
                "Awaiting reg" (usually due to legacy mixed-case emails). */}
            <RepairCandidateLinkPanel />
        </div>
    );
}

function RepairCandidateLinkPanel() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [sweeping, setSweeping] = useState(false);
    const [result, setResult] = useState(null);

    const repairOne = async () => {
        const target = email.trim().toLowerCase();
        if (!target) {
            toast.error("Enter a candidate email to repair.");
            return;
        }
        setLoading(true);
        setResult(null);
        try {
            const { data } = await api.post(
                "/superadmin/candidates/relink",
                { email: target },
            );
            setResult({ ...data, mode: "single" });
            if (data.linked > 0 || data.drips_stopped > 0) {
                toast.success(
                    `Linked ${data.linked} record${data.linked === 1 ? "" : "s"}, ` +
                    `stopped ${data.drips_stopped} drip${data.drips_stopped === 1 ? "" : "s"}.`,
                );
            } else {
                toast.info("No dangling rows found for this email — already linked.");
            }
        } catch (ex) {
            const msg = formatApiError(ex);
            toast.error(msg || "Repair failed. Check the email and try again.");
        } finally {
            setLoading(false);
        }
    };

    const sweepAll = async () => {
        if (!window.confirm(
            "Sweep every dangling candidate row across the platform? " +
            "This scans up to 2000 records and re-links any candidate whose " +
            "email matches an existing users row (case-insensitive)."
        )) return;
        setSweeping(true);
        setResult(null);
        try {
            const { data } = await api.post(
                "/superadmin/candidates/relink",
                {},
            );
            setResult({ ...data, mode: "sweep" });
            if (data.relinked_count > 0) {
                toast.success(
                    `Swept ${data.swept_emails} emails — relinked ${data.total_linked} ` +
                    `candidate row${data.total_linked === 1 ? "" : "s"}.`,
                );
            } else {
                toast.info(`Swept ${data.swept_emails} emails — none needed repair.`);
            }
        } catch (ex) {
            toast.error(formatApiError(ex) || "Sweep failed.");
        } finally {
            setSweeping(false);
        }
    };

    return (
        <div className="mt-10 pt-8 border-t border-[#E5E5E5]" data-testid="repair-candidate-link-panel">
            <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-cobalt text-white flex items-center justify-center shrink-0">
                    <LinkSimple size={22} weight="bold" />
                </div>
                <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">
                        Admin · Candidate link repair
                    </p>
                    <h2 className="font-heading text-2xl font-black tracking-tight text-ink">
                        Repair &quot;Awaiting reg&quot; candidates
                    </h2>
                    <p className="text-sm text-[#525252] mt-2 max-w-2xl">
                        Fixes candidates who registered but still show
                        <span className="font-mono bg-[#F5F5F5] px-1"> Awaiting reg</span>
                        on the Kanban. Runs the case-insensitive relink between
                        the <span className="font-mono">users</span> table and
                        the <span className="font-mono">candidates</span> table.
                    </p>
                </div>
            </div>

            <div className="border border-[#0A0A0A] bg-white p-6">
                <label className="text-[11px] font-black uppercase tracking-[0.2em] text-ink mb-2 block">
                    Candidate email
                </label>
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 flex items-center gap-2 border border-[#E5E5E5] px-3">
                        <User size={14} weight="bold" className="text-[#A3A3A3]" />
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="aswinisb17@gmail.com"
                            data-testid="repair-link-email-input"
                            disabled={loading || sweeping}
                            className="flex-1 py-2.5 text-sm bg-transparent focus:outline-none placeholder:text-[#A3A3A3]"
                            onKeyDown={(e) => { if (e.key === "Enter") repairOne(); }}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={repairOne}
                        disabled={loading || sweeping || !email.trim()}
                        data-testid="repair-link-repair-btn"
                        className="bg-cobalt hover:bg-cobalt-dark disabled:opacity-40 disabled:cursor-not-allowed text-white px-5 py-2.5 text-xs font-black uppercase tracking-wider inline-flex items-center justify-center gap-2 transition-colors"
                    >
                        <LinkSimple size={14} weight="bold" />
                        {loading ? "Repairing…" : "Repair this email"}
                    </button>
                </div>

                <div className="mt-5 pt-5 border-t border-dashed border-[#E5E5E5]">
                    <p className="text-xs text-[#525252] mb-3">
                        Or sweep everyone at once — safe, idempotent, bounded to 2000 rows:
                    </p>
                    <button
                        type="button"
                        onClick={sweepAll}
                        disabled={loading || sweeping}
                        data-testid="repair-link-sweep-btn"
                        className="bg-ink hover:bg-[#171717] disabled:opacity-40 text-white px-5 py-2.5 text-xs font-black uppercase tracking-wider inline-flex items-center gap-2 transition-colors"
                    >
                        <ArrowClockwise size={14} weight="bold" />
                        {sweeping ? "Sweeping…" : "Sweep all dangling candidates"}
                    </button>
                </div>
            </div>

            {result && (
                <div
                    data-testid="repair-link-result"
                    className="mt-4 border border-[#E5E5E5] bg-white p-5"
                >
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-3">
                        Result · {result.mode === "sweep" ? "Sweep" : "Single email"}
                    </p>
                    {result.mode === "single" ? (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <StatCard label="Email" value={result.email || "—"} tone="neutral" small />
                            <StatCard label="Rows linked" value={result.linked ?? 0} tone={result.linked > 0 ? "ok" : "neutral"} />
                            <StatCard label="Drips stopped" value={result.drips_stopped ?? 0} tone={result.drips_stopped > 0 ? "ok" : "neutral"} />
                            <StatCard label="User ID" value={(result.user_id || "").slice(0, 8) || "—"} tone="neutral" small />
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                <StatCard label="Emails swept" value={result.swept_emails ?? 0} tone="neutral" />
                                <StatCard label="Relinked" value={result.relinked_count ?? 0} tone={result.relinked_count > 0 ? "ok" : "neutral"} />
                                <StatCard label="Rows linked" value={result.total_linked ?? 0} tone={result.total_linked > 0 ? "ok" : "neutral"} />
                                <StatCard label="Drips stopped" value={result.total_drips_stopped ?? 0} tone={result.total_drips_stopped > 0 ? "ok" : "neutral"} />
                            </div>
                            {result.sample?.length > 0 && (
                                <div className="mt-4 border border-[#E5E5E5] overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] bg-[#FAFAFA]">
                                                <th className="px-3 py-2">Email</th>
                                                <th className="px-3 py-2">Linked</th>
                                                <th className="px-3 py-2">Drips stopped</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {result.sample.map((s, i) => (
                                                <tr key={i} className="border-t border-[#E5E5E5]">
                                                    <td className="px-3 py-2 font-mono text-[11px]">{s.email}</td>
                                                    <td className="px-3 py-2 font-bold text-[#059669]">{s.linked}</td>
                                                    <td className="px-3 py-2 font-bold text-[#059669]">{s.drips_stopped}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function StatCard({ label, value, tone, testid, small }) {
    const cls =
        tone === "ok"    ? "bg-[#ECFDF5] text-[#059669] border-[#A7F3D0]" :
        tone === "warn"  ? "bg-[#FFFBEB] text-[#B45309] border-[#FDE68A]" :
                           "bg-[#F5F5F5] text-ink border-[#E5E5E5]";
    return (
        <div
            data-testid={testid}
            className={`border p-4 ${cls}`}
        >
            <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-80">{label}</p>
            <p className={`font-heading font-black tracking-tight mt-1 ${small ? "text-sm break-all font-mono" : "text-4xl"}`}>
                {value}
            </p>
        </div>
    );
}
